<i18n lang="json">
{
  "en": {
    "tryBeforeYouBuy": {
      "title": "Try one before you buy one",
      "content": "Explore similar RVs nearby to test them before you make a decision.",
      "cta": "Browse Rental RVs on RVezy"
    },
    "visitPopSells": {
      "cta": "Visit PopSells"
    },
    "rvEarnings": {
      "title": "This RV can pay for itself!",
      "contentBefore": "You could earn",
      "contentAmount": "{amount}/{period}",
      "contentAfter": "on average renting this RV.",
      "cta": "See similar RVs",
      "tooltip": "RVs are more affordable than you think, click learn more to see hundreds of RVs in your area that are paying themselves off by renting them out only a couple of times per month."
    },
    "metadata": {
      "title": "{name} for sale in {city}, {region} | RVezy",
      "description": {
        "withPrice": "{listingCondition} {class} for sale in {city}, {region}. {price}. RV sold by {dealershipName}. View pictures and specs. Contact seller for more information.",
        "withoutPrice": "{listingCondition} {class} for sale in {city}, {region}. RV sold by {dealershipName}. View pictures and specs. Contact seller for more information."
      }
    },
    "monthly": "monthly"
  },
  "fr": {
    "tryBeforeYouBuy": {
      "title": "Essayez-en un avant de l'acheter",
      "content": "Explorez des VR similaires à proximité pour les tester avant de prendre une décision.",
      "cta": "Parcourir les VR à louer sur RVezy"
    },
    "visitPopSells": {
      "cta": "Visiter PopSells"
    },
    "rvEarnings": {
      "title": "Ce VR peut se payer tout seul !",
      "contentBefore": "Vous pourriez gagner",
      "contentAmount": "{amount}/{period}",
      "contentAfter": "en moyenne en louant ce VR.",
      "cta": "Voir des VR similaires",
      "tooltip": "Les VR sont plus abordables que vous ne le pensez, cliquez sur en savoir plus pour voir des centaines de VR dans votre région qui se remboursent en les louant seulement quelques fois par mois."
    },
    "metadata": {
      "title": "{name} à vendre à {city}, {region} | RVezy",
      "description": {
        "withPrice": "{class} {listingCondition} à vendre à {city}, {region}. {price}. VR vendu par {dealershipName}. Voir photos et détails. Contactez le vendeur pour plus d’infos.",
        "withoutPrice": "{class} {listingCondition} à vendre à {city}, {region}. VR vendu par {dealershipName}. Voir photos et détails. Contactez le vendeur pour plus d’infos."
      }
    },
    "monthly": "mensuellement"
  }
}
</i18n>
<i18n lang="yaml" src="~/i18n/rv-category.yml"></i18n>
<i18n lang="yaml" src="~/i18n/rv-type.yml"></i18n>
<i18n lang="json" src="~/i18n/listing-condition.json"></i18n>

<template>
  <div class="bg-primary-50">
    <div v-if="rv" class="px-4 py-0 mx-auto max-w-[72rem] mb-0 lg:grid lg:grid-cols-12 lg:gap-6 lg:p-6">
      <div class="lg:col-span-8">
        <SectionCard v-if="isLargeScreen && rv.images?.length" :padded="false">
          <RvDetailsGallery :images="rv.images" />
        </SectionCard>

        <template v-if="!isLargeScreen">
          <RvDetailsGallery v-if="rv.images?.length" :images="rv.images" class="w-dvw relative -left-4 rounded-none">
            <div
              class="flex size-8 items-center justify-center bg-white absolute top-4 left-4 rounded-[50%] cursor-pointer"
            >
              <NuxtLink
                :to="
                  getLocalePath({
                    name: 'search',
                    query: {
                      searchAddress: [rv.location?.city, rv.location?.regionCode, rv.location?.countryCode]
                        .filter((x) => x)
                        .join(', ')
                    }
                  })
                "
              >
                <IconChevron direction="left" class="size-4" />
              </NuxtLink>
            </div>
          </RvDetailsGallery>

          <SectionCard>
            <h1 class="typography-subtitle-1 mb-3">{{ rv.name }}</h1>
            <RvDetailsPrice
              :asking-price="rv.listPrice ?? undefined"
              :msrp-price="rv.msrpPrice ?? undefined"
              :country="rv.location?.countryCode"
            />

            <Notice v-if="rv.averageMonthlyRate">
              <template #title>
                <div class="flex items-center gap-1">
                  {{ t('rvEarnings.title') }}

                  <IconCircleQuestionMark
                    class="text-highlight cursor-pointer"
                    @click="toggleRvEarningsTooltip"
                    ref="rvEarningsTooltipIcon"
                  />
                  <ClientOnly>
                    <Tooltip
                      :reference-element="rvEarningsTooltipIcon"
                      :show="showRvEarningsTooltip"
                      :content="t('rvEarnings.tooltip')"
                      @close="toggleRvEarningsTooltip(false)"
                    />
                  </ClientOnly>
                </div>
              </template>
              <template #content>
                {{ t('rvEarnings.contentBefore') }}
                <strong>{{
                  t('rvEarnings.contentAmount', {
                    amount: useCurrency().getValueWithCurrency({
                      amount: rv.averageMonthlyRate,
                      country: rv.location?.countryCode
                    }),
                    period: t('monthly')
                  })
                }}</strong>
                {{ t('rvEarnings.contentAfter') }}

                <NuxtLink
                  :href="destinationUrl"
                  target="_blank"
                  class="typography-link-1"
                  @click.prevent="handleRvEarningsClick"
                  >{{ t('rvEarnings.cta') }}</NuxtLink
                >
              </template>
            </Notice>
          </SectionCard>
        </template>

        <SectionCard>
          <RvDetailsAttributes :rv="rv" />
        </SectionCard>

        <SectionCard v-if="rv.description || !isLargeScreen">
          <div class="mb-4 lg:mb-0">
            <RvDetailsAbout v-if="rv.description" :description="rv.description" />
          </div>
          <template v-if="!isLargeScreen">
            <Notice v-if="!isPopSells" class="mb-4">
              <template #icon>
                <IconDrivable class="text-primary-500 size-8" />
              </template>
              <template #title>
                {{ t('tryBeforeYouBuy.title') }}
              </template>
              <template #content>
                {{ t('tryBeforeYouBuy.content') }}
              </template>
            </Notice>
            <FormButton tag="a" :href="buyCtaUrl" target="_blank" @click.prevent="handleBuyCtaClick">{{
              buyCtaLabel
            }}</FormButton>

            <Divider />

            <RvDetailsSellerProfile :name="rv.dealer?.name!" :image="rv.dealer?.logoUrl!" />
          </template>
        </SectionCard>

        <SectionCard v-if="brandSummary">
          <RvDetailsBrandSummary
            :title="brandSummary.title"
            :image="brandSummary.feature_image"
            :image-alt="brandSummary.feature_image_alt"
            :html-content="brandSummary.html"
            :slug="slugfiedMake"
            @learn-more-clicked="brandClickHandler"
          />
        </SectionCard>
      </div>

      <div v-if="isLargeScreen" class="lg:col-span-4">
        <SectionCard class="sticky top-[6.5rem]">
          <h1 class="typography-subtitle-1 mb-3">{{ rv.name }}</h1>
          <RvDetailsPrice
            :asking-price="rv.listPrice ?? undefined"
            :msrp-price="rv.msrpPrice ?? undefined"
            :country="rv.location?.countryCode"
          />

          <Notice v-if="rv.averageMonthlyRate" class="mb-5">
            <template #title>
              <div class="flex items-center gap-1">
                {{ t('rvEarnings.title') }}

                <IconCircleQuestionMark
                  class="text-highlight cursor-pointer"
                  @click="toggleRvEarningsTooltip"
                  ref="rvEarningsTooltipIcon"
                />
                <ClientOnly>
                  <Tooltip
                    :reference-element="rvEarningsTooltipIcon"
                    :show="showRvEarningsTooltip"
                    :content="t('rvEarnings.tooltip')"
                    @close="toggleRvEarningsTooltip(false)"
                  />
                </ClientOnly>
              </div>
            </template>
            <template #content>
              {{ t('rvEarnings.contentBefore') }}
              <strong>{{
                t('rvEarnings.contentAmount', {
                  amount: useCurrency().getValueWithCurrency({
                    amount: rv.averageMonthlyRate,
                    country: rv.location?.countryCode
                  }),
                  period: t('monthly')
                })
              }}</strong>
              {{ t('rvEarnings.contentAfter') }}

              <NuxtLink
                :href="destinationUrl"
                target="_blank"
                class="typography-link-1"
                @click.prevent="handleRvEarningsClick"
                >{{ t('rvEarnings.cta') }}</NuxtLink
              >
            </template>
          </Notice>

          <Divider />

          <Notice v-if="!isPopSells">
            <template #icon>
              <IconDrivable class="text-primary-500 size-8" />
            </template>
            <template #title>
              {{ t('tryBeforeYouBuy.title') }}
            </template>
            <template #content>
              {{ t('tryBeforeYouBuy.content') }}
            </template>
          </Notice>
          <FormButton tag="a" :href="buyCtaUrl" target="_blank" @click.prevent="handleBuyCtaClick">{{
            buyCtaLabel
          }}</FormButton>

          <Divider />

          <RvDetailsSellerProfile :name="rv.dealer?.name!" :image="rv.dealer?.logoUrl!" />
        </SectionCard>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { IconCircleQuestionMark } from '#build/components'
import { CurrencyCode, CurrencyCodeByCountry } from '~/enums/i18n'
import { SegmentEvents } from '~/enums/segment-events'
import { captureError } from '~/lib/logger'
import { segmentTrack, trackRvEvent } from '~/lib/tracking'

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/rv/[slug]',
    fr: '/vr/[slug]'
  }
})

const isLargeScreen = useIsLargeScreen()
const route = useRoute()
const { locale, t, te } = useI18n()
const appConfig = useRuntimeConfig().public.app
const { getMarketplaceApiClient } = useApiClients()
const { getGhostPageContent } = useGhost()

// this causes a server side warning, but that's ok
setPageLayout(isLargeScreen.value ? 'default' : 'headerless')

const showRvEarningsTooltip = ref(false)
const rvEarningsTooltipIcon = ref<InstanceType<typeof IconCircleQuestionMark>>()

const { error: fetchError, data: rv } = await useAsyncData('rv-details', async () => {
  const { data: rvResponse } = await getMarketplaceApiClient().GET('/Rv/{id}', {
    params: {
      path: {
        id: route.params.slug as string
      }
    }
  })

  return rvResponse
})
if (fetchError.value) captureError(toRaw(fetchError.value))

if (!rv.value) await localeNavigateTo({ name: 'index' }, { redirectCode: 301 })

const slugfiedMake = computed(() => slugify(rv.value?.make ?? 'unknown'))
const searchUrl = computed(() => {
  if (!rv.value) return `${appConfig.rentals.webUrl}/rv-search`
  return getSimilarRvsRentalLink(rv.value)
})

const isPopSells = computed(() => rv.value?.dealer?.websiteUrl?.toLowerCase().includes('popsells.com') ?? false)
const buyCtaUrl = computed(() => (isPopSells.value ? (rv.value?.dealer?.websiteUrl ?? '') : searchUrl.value))
const buyCtaLabel = computed(() => (isPopSells.value ? t('visitPopSells.cta') : t('tryBeforeYouBuy.cta')))

const destinationUrl = computed(() => {
  const domain = appConfig.rentals.webUrl

  const country = rv.value?.location?.countryCode === 'US' ? locale.value === 'fr' ? 'etats-unis' : 'united-states' : 'canada'
  const region = lookupIsoCodeToSlug(rv.value?.location?.regionCode, locale.value)
  const destinationPath = [country, region && slugify(region), slugify(rv.value?.location?.city ?? '')].filter(
    Boolean
  ).join('/')

  if (locale.value === 'fr') {
    return `${domain}/fr/locations-vr/destination/${destinationPath}`
  }

  return `${domain}/rv-rentals/destination/${destinationPath}`
})

const { data: brandSummary, error: brandSummaryError } = await useLazyAsyncData('content', async () => {
  if (rv.value?.make) {
    return await getGhostPageContent({ slug: `${slugfiedMake.value}-summary`, tag: 'brand-summary' })
  }
})

if (brandSummaryError.value)
  captureError(toRaw(brandSummaryError.value) ?? new Error('Error getting results on ghost for brand summary!'))

const brandClickHandler = () => {
  segmentTrack(SegmentEvents.ManufacturerLearnMoreClicked, { rvId: rv.value?.id }, 'listing-page')
}

const trackListingViewed = () => {
  if (!rv.value) return

  trackRvEvent(SegmentEvents.ListingViewed, rv.value, {
    cta: route.query.cta
  })
}

const handleBuyCtaClick = async ({ currentTarget: target }: { currentTarget: HTMLAnchorElement }) => {
  segmentTrack(
    SegmentEvents.CTAClicked,
    isPopSells.value
      ? {
          name: 'visit-popsells',
          cta: 'visit-popsells'
        }
      : {
          name: 'search-similar-rvs',
          cta: 'try-before-you-buy'
        }
  )

  await windowNavigateTo(target)
}

const handleRvEarningsClick = async ({ currentTarget: target }: { currentTarget: HTMLAnchorElement }) => {
  segmentTrack(SegmentEvents.CTAClicked, {
    name: 'search-similar-rvs',
    cta: 'rv-earnings'
  })

  await windowNavigateTo(target)
}

const toggleRvEarningsTooltip = (open?: boolean) => {
  showRvEarningsTooltip.value = open ?? !showRvEarningsTooltip.value
}

useServerHead({
  link: [
    {
      rel: 'canonical',
      href: `${appConfig.marketplace.webUrl}${getLocalePath({ name: 'rv-slug', params: { slug: rv.value?.slug } })}`,
      hid: 'i18n-can'
    }
  ]
})

const translationObject = {
  listingCondition: t(`ListingCondition.${rv.value?.listingCondition}`),
  class: t(`RVType.${rv.value?.class}`),
  city: rv.value?.location?.city,
  region: rv.value?.location?.regionCode,
  dealershipName: rv.value?.dealer?.name
}

useServerSeoMeta({
  title: t('metadata.title', {
    name: rv.value?.name,
    city: rv.value?.location?.city,
    region: rv.value?.location?.regionCode
  }),
  description: rv.value?.listPrice
    ? t('metadata.description.withPrice', {
        ...translationObject,
        price: useCurrency().getValueWithCurrency({
          amount: rv.value?.listPrice,
          country: rv.value?.location?.countryCode
        })
      })
    : t('metadata.description.withoutPrice', translationObject)
})

useJsonld(() => {
  const Rv = rv.value
  if (Rv == null) return

  const jsonLd: Parameters<typeof useJsonld>[0] = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    sku: Rv.id,
    name: Rv.name as string,
    description: Rv.description as string,
    isFamilyFriendly: true,
    itemCondition:
      Rv.listingCondition === 'New' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
    additionalProperty: [],
    image: Rv.images?.at(0),
    model: Rv.model as string
  }

  if (Rv.make)
    jsonLd.brand = {
      '@type': 'Brand',
      name: Rv.make
    }

  const category = ['Recreational vehicle']
  if (Rv.category) category.push(te(`RVCategory.${Rv.category}`) ? t(`RVCategory.${Rv.category}`) : Rv.category)
  if (Rv.class) category.push(te(`RVType.${Rv.class}`) ? t(`RVType.${Rv.class}`) : Rv.class)
  jsonLd.category = category.join(' > ')

  if (Rv.length)
    jsonLd.depth = {
      '@type': 'QuantitativeValue',
      unitCode: 'FOT',
      value: Math.round(metersToFeet(Rv.length))
    }

  if (Rv.weight)
    jsonLd.weight = {
      '@type': 'QuantitativeValue',
      unitCode: 'LBR',
      value: Math.round(kilogramsToPounds(Rv.weight))
    }

  if (Rv.listPrice)
    jsonLd.offers = [
      {
        '@type': 'Offer',
        availability: 'https://schema.org/InStoreOnly',
        price: Rv.listPrice,
        priceCurrency:
          CurrencyCodeByCountry[Rv.location?.countryCode as unknown as keyof typeof CurrencyCodeByCountry] ??
          CurrencyCode.CAD
      }
    ]

  const additionalProperties = []
  if (Rv.bathrooms)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Bathrooms',
      value: String(Rv.bathrooms)
    })

  if (Rv.beds)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Beds',
      value: String(Rv.beds)
    })

  if (Rv.slideOuts)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Slideouts',
      value: String(Rv.slideOuts)
    })

  if (Rv.seatBelts)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Seat belts',
      value: String(Rv.seatBelts)
    })

  if (Rv.trim)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Trim',
      value: Rv.trim
    })

  if (Rv.sleepingCapacity)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Sleeping capacity',
      value: String(Rv.sleepingCapacity)
    })

  if (Rv.mileage)
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Mileage',
      value: String(Rv.mileage)
    })

  if (additionalProperties.length) jsonLd.additionalProperty = additionalProperties as any

  return jsonLd as any
})

onMounted(() => {
  if (isLargeScreen.value) setPageLayout('default')
  trackListingViewed()
})

watch(
  () => rv.value,
  () => {
    trackListingViewed()
  }
)

watch(
  () => isLargeScreen.value,
  (t) => {
    setPageLayout(t ? 'default' : 'headerless')
  }
)
</script>
